import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  increment,
  query,
  orderBy,
  limit as firestoreLimit,
} from "firebase/firestore";
import { db } from "./firebaseConfig";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowUp,
  CalendarDays,
  GraduationCap,
  Search,
  ShieldCheck,
  Sparkles,
  UserCog,
  UserRound,
  Users,
} from "lucide-react";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import "./ManageUsers.css";

const TEACHER_TRIAL_DURATION_DAYS = 28;

const parseFirestoreDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  if (ts instanceof Date) return ts;
  const parsed = new Date(ts);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const ManageUsers = ({ user }) => {
  // ── Existing state ────────────────────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("newest");
  const [isLoading, setIsLoading] = useState(true);
  const [creditInputs, setCreditInputs] = useState({});
  const [creditLoading, setCreditLoading] = useState({});
  const [creditMessages, setCreditMessages] = useState({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [creditUsageLogs, setCreditUsageLogs] = useState([]);
  const [creditUsageLoading, setCreditUsageLoading] = useState(false);
  const [creditUsageError, setCreditUsageError] = useState("");
  const [creditUsageSearch, setCreditUsageSearch] = useState("");
  const [creditUsageActionFilter, setCreditUsageActionFilter] = useState("all");
  const [creditUsageHighlighted, setCreditUsageHighlighted] = useState(false);
  const [showCreditUsage, setShowCreditUsage] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true"
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── Modal state ───────────────────────────────────────────────────────────────
  const [modalUserId, setModalUserId] = useState(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [modalActionMsg, setModalActionMsg] = useState(null);
  const [modalExtraTimeInput, setModalExtraTimeInput] = useState("0");
  const [modalExtraTimeMsg, setModalExtraTimeMsg] = useState(null);

  const navigate = useNavigate();
  const creditUsageSectionRef = useRef(null);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute(
      "data-dark-mode",
      darkMode ? "true" : "false"
    );
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Derived: always in sync with the latest users array
  const modalUser = modalUserId
    ? users.find((u) => u.id === modalUserId) ?? null
    : null;

  // ── Fetch users ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const fetchedUsers = querySnapshot.docs.map((docSnap) => {
          const data = docSnap.data();

          // creditsRemaining — safe parse
          const creditsValue =
            typeof data.creditsRemaining === "number"
              ? data.creditsRemaining
              : Number(data.creditsRemaining) || 0;

          // creditsSpent — safe parse, default 0 if missing/null/invalid
          const rawSpent = data.creditsSpent;
          const creditsSpentValue =
            typeof rawSpent === "number" &&
            isFinite(rawSpent) &&
            rawSpent >= 0
              ? Math.round(rawSpent)
              : 0;

          // Helper: parse any Firestore timestamp, Date, or seconds object → JS Date
          const parseTs = (ts) => {
            if (!ts) return null;
            if (typeof ts.toDate === "function") return ts.toDate();
            if (ts.seconds) return new Date(ts.seconds * 1000);
            if (ts instanceof Date) return ts;
            const p = new Date(ts);
            return isNaN(p.getTime()) ? null : p;
          };

          return {
            id: docSnap.id,
            email: data.email || "N/A",
            role: data.role || "N/A",
            username: data.username || data.displayName || "N/A",
            extraTimePercentage: data.extraTimePercentage ?? 0,
            creditsRemaining: creditsValue,
            creditsSpent: creditsSpentValue,
            createdAt: parseTs(data.createdAt),
            // New fields — all optional/nullable
            teacherStatus: data.teacherStatus ?? null,
            trialStartedAt: parseTs(data.trialStartedAt),
            trialEndsAt: parseTs(data.trialEndsAt),
            onboardingCompleted: data.onboardingCompleted ?? false,
            teacherCode: data.teacherCode ?? null,
            tenant: data.tenant ?? null,
          };
        });

        // Default sort: newest first
        fetchedUsers.sort((a, b) => {
          const aTime = a.createdAt ? a.createdAt.getTime() : -Infinity;
          const bTime = b.createdAt ? b.createdAt.getTime() : -Infinity;
          return bTime - aTime;
        });

        setUsers(fetchedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
        alert("Failed to fetch users. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return;

    const fetchCreditUsageLogs = async () => {
      setCreditUsageLoading(true);
      setCreditUsageError("");

      try {
        const logsQuery = query(
          collection(db, "creditUsageLogs"),
          orderBy("createdAt", "desc"),
          firestoreLimit(100)
        );
        const snapshot = await getDocs(logsQuery);
        const logs = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            createdAt: parseFirestoreDate(data.createdAt),
          };
        });

        setCreditUsageLogs(logs);
      } catch (error) {
        console.error("Error fetching credit usage logs:", error);
        setCreditUsageError("Failed to load credit usage logs.");
      } finally {
        setCreditUsageLoading(false);
      }
    };

    fetchCreditUsageLogs();
  }, [user?.role]);

  // ── Scroll tracking (back-to-top button) ──────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ── Date helpers ──────────────────────────────────────────────────────────────
  const formatDate = (date) => {
    if (!date) return "—";
    try {
      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (_) {
      return "—";
    }
  };

  const isJoinedThisWeek = (u) => {
    if (!u.createdAt) return false;
    return u.createdAt >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  };

  const daysFromNow = (date) => {
    if (!date) return null;
    const diff = date.getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  // ── Statistics ────────────────────────────────────────────────────────────────
  const userStats = {
    total: users.length,
    students: users.filter((u) => u.role === "student").length,
    teachers: users.filter((u) => u.role === "teacher").length,
    admins: users.filter((u) => u.role === "admin").length,
    newThisWeek: users.filter(isJoinedThisWeek).length,
    activeTrials: users.filter(
      (u) =>
        u.role === "teacher" &&
        u.teacherStatus === "trial" &&
        daysFromNow(u.trialEndsAt) !== null &&
        daysFromNow(u.trialEndsAt) >= 0
    ).length,
  };

  // ── Filter → Search → Sort ────────────────────────────────────────────────────
  const getDisplayedUsers = () => {
    let result = [...users];

    if (selectedFilter === "students")
      result = result.filter((u) => u.role === "student");
    else if (selectedFilter === "teachers")
      result = result.filter((u) => u.role === "teacher");
    else if (selectedFilter === "admins")
      result = result.filter((u) => u.role === "admin");
    else if (selectedFilter === "joined-this-week")
      result = result.filter(isJoinedThisWeek);

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (u) =>
          u.username.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term) ||
          u.role.toLowerCase().includes(term)
      );
    }

    if (sortOption === "newest") {
      result.sort(
        (a, b) =>
          (b.createdAt?.getTime() ?? -Infinity) -
          (a.createdAt?.getTime() ?? -Infinity)
      );
    } else if (sortOption === "oldest") {
      result.sort(
        (a, b) =>
          (a.createdAt?.getTime() ?? Infinity) -
          (b.createdAt?.getTime() ?? Infinity)
      );
    } else if (sortOption === "name-az") {
      result.sort((a, b) => a.username.localeCompare(b.username));
    } else if (sortOption === "name-za") {
      result.sort((a, b) => b.username.localeCompare(a.username));
    } else if (sortOption === "role") {
      result.sort((a, b) => a.role.localeCompare(b.role));
    }

    return result;
  };

  const displayedUsers = getDisplayedUsers();

  const creditActionOptions = [
    { value: "all", label: "All actions" },
    { value: "autoReport", label: "AutoReport" },
    { value: "markQuiz", label: "Test Marking" },
    { value: "generateQuizQuestions", label: "Generate Test Questions" },
    { value: "generateAdditionalQuestion", label: "Generate Additional Question" },
    { value: "paperBatchSubmission", label: "Paper Batch Submission" },
  ];

  const getCreditActionKey = (actionType = "") => {
    const normalized = String(actionType || "").trim();
    if (
      normalized === "markQuiz" ||
      normalized === "markAll" ||
      normalized === "markUnmarked" ||
      normalized.toLowerCase().includes("mark")
    ) {
      return "markQuiz";
    }
    return normalized;
  };

  const formatActionType = (actionType = "") => {
    const key = getCreditActionKey(actionType);
    const match = creditActionOptions.find((option) => option.value === key);
    if (match && match.value !== "all") return match.label;
    if (!actionType) return "Unknown";
    return String(actionType)
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (char) => char.toUpperCase());
  };

  const formatDateTime = (date) => {
    if (!date) return "--";
    try {
      return date.toLocaleString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (_) {
      return "--";
    }
  };

  const pluralize = (count, singular, plural = `${singular}s`) =>
    `${count} ${Number(count) === 1 ? singular : plural}`;

  const joinDetailParts = (parts) => parts.filter(Boolean).join(" - ");

  const formatCreditUsageDetails = (log) => {
    const metadata = log.metadata || {};
    const key = getCreditActionKey(log.actionType);

    if (key === "autoReport") {
      const count = metadata.reportCount ?? metadata.studentCount;
      return joinDetailParts([
        count != null ? pluralize(count, "report") : null,
        metadata.className,
        metadata.subject,
      ]) || "AutoReport generation";
    }

    if (key === "generateQuizQuestions") {
      return joinDetailParts([
        metadata.subject,
        metadata.topic,
        metadata.numQuestions != null ? pluralize(metadata.numQuestions, "question") : null,
      ]) || "Question generation";
    }

    if (key === "markQuiz") {
      const count = metadata.submissionCount;
      return count != null ? pluralize(count, "submission") : "Test marking";
    }

    if (key === "paperBatchSubmission") {
      return joinDetailParts([
        metadata.quizTitle,
        metadata.studentCount != null ? pluralize(metadata.studentCount, "student") : null,
        metadata.totalPages != null ? pluralize(metadata.totalPages, "page") : null,
        metadata.fullAnalysisEnabled === true
          ? "Full analysis on"
          : metadata.fullAnalysisEnabled === false
          ? "Full analysis off"
          : null,
      ]) || "Paper batch";
    }

    if (key === "generateAdditionalQuestion") {
      return joinDetailParts([metadata.subject, metadata.topic]) || "Additional question";
    }

    return metadata && Object.keys(metadata).length > 0 ? "Metadata available" : "--";
  };

  const formatCreditValue = (value) =>
    typeof value === "number" && Number.isFinite(value) ? value : "--";

  const displayedCreditUsageLogs = creditUsageLogs.filter((log) => {
    const term = creditUsageSearch.trim().toLowerCase();
    const actionKey = getCreditActionKey(log.actionType);
    const matchesAction =
      creditUsageActionFilter === "all" || actionKey === creditUsageActionFilter;

    if (!matchesAction) return false;
    if (!term) return true;

    return [
      log.userName,
      log.userEmail,
      log.userId,
      formatActionType(log.actionType),
      formatCreditUsageDetails(log),
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  });

  // ── Design tokens ─────────────────────────────────────────────────────────────
  const handleViewCreditUsageForUser = (targetUser) => {
    if (!targetUser) return;

    const preferredSearch =
      targetUser.email && targetUser.email !== "N/A"
        ? targetUser.email
        : targetUser.username || "";

    setCreditUsageSearch(preferredSearch);
    setCreditUsageActionFilter("all");
    setShowCreditUsage(true);
    setCreditUsageHighlighted(true);
    closeModal();

    window.setTimeout(() => {
      creditUsageSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);

    window.setTimeout(() => {
      setCreditUsageHighlighted(false);
    }, 1800);
  };

  const roleClassFor = (role) => `mu-role-badge mu-role-${role || "unknown"}`;

  const PRIMARY = "#235ce3";
  const PRIMARY_DARK = "#235ce3";
  const PRIMARY_LIGHT_BG = "#eaf3ff";
  const PRIMARY_LIGHTER_BG = "#f5f8fd";
  const isDarkMode = darkMode;
  const creditUsageTheme = {
    surface: isDarkMode ? "#0f1b2d" : "#ffffff",
    surfaceSoft: isDarkMode ? "#13233a" : "#f8fbff",
    border: isDarkMode ? "rgba(125, 211, 252, 0.18)" : "#dbeaf3",
    borderSoft: isDarkMode ? "rgba(148, 163, 184, 0.16)" : "#e3edf5",
    title: isDarkMode ? "#f8fafc" : PRIMARY_DARK,
    text: isDarkMode ? "#cbd5e1" : "#5f7180",
    accent: isDarkMode ? "#38bdf8" : PRIMARY,
    accentSoft: isDarkMode ? "rgba(56, 189, 248, 0.12)" : PRIMARY_LIGHTER_BG,
    shadow: isDarkMode
      ? "0 18px 42px rgba(0,0,0,0.35)"
      : "0 10px 28px rgba(41,128,185,0.10)",
  };

  const getRoleBadge = (role) => ({
    backgroundColor:
      role === "admin" ? "#f7dd8f" : role === "teacher" ? "#c2dbff" : "#d6eaf8",
    color:
      role === "admin" ? "#876600" : role === "teacher" ? "#0b5cad" : PRIMARY_DARK,
  });
  // ── Role badge style ──────────────────────────────────────────────────────────
  const avatarToneFor = (value) => {
    const total = Array.from(String(value || "user")).reduce(
      (sum, character) => sum + character.charCodeAt(0),
      0
    );
    return `mu-avatar-tone-${total % 5}`;
  };

  const initialsFor = (value) =>
    String(value || "User")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

  // ── Status info (label + badge colours) ──────────────────────────────────────
  const getStatusInfo = (u) => {
    if (u.role === "admin")
      return { label: "Admin", bg: "#f7dd8f", color: "#876600" };
    if (u.role === "teacher") {
      const ts = u.teacherStatus;
      
      // Debug log for TestAcc102
      if (u.email && u.email.includes("102")) {
        console.log("TestAcc102 status debug:", {
          email: u.email,
          role: u.role,
          teacherStatus: u.teacherStatus,
          trialEndsAt: u.trialEndsAt,
          calculatedStatus: { ts, daysFromNow: daysFromNow(u.trialEndsAt) }
        });
      }
      
      // Check if explicitly marked as expired
      if (ts === "expired") {
        return { label: "Expired Trial", bg: "#f8d7da", color: "#721c24" };
      }
      
      // Check if on trial but expired
      if (ts === "trial") {
        const days = daysFromNow(u.trialEndsAt);
        if (days === null || days < 0) {
          // Trial has expired
          return { label: "Expired Trial", bg: "#f8d7da", color: "#721c24" };
        }
        // Trial still active
        return { label: `Trial · ${days}d left`, bg: "#cce5ff", color: "#004085" };
      }
      
      // Active teacher
      if (ts === "active") {
        return { label: "Active", bg: "#d4edda", color: "#155724" };
      }
      
      // Suspended teacher
      if (ts === "suspended") {
        return { label: "Suspended", bg: "#f8d7da", color: "#721c24" };
      }
      
      // Inactive teacher
      if (ts === "inactive") {
        return { label: "Inactive", bg: "#e2e3e5", color: "#495057" };
      }
      
      // No valid teacherStatus found - pending setup
      return { label: "Pending", bg: "#fff3cd", color: "#856404" };
    }
    return null; // student → "—"
  };

  // ── Shared td style ───────────────────────────────────────────────────────────
  const statusClassFor = (account, statusInfo) => {
    if (account.role === "admin") return "mu-status-admin";
    if (!statusInfo) return "mu-status-neutral";
    if (statusInfo.label.startsWith("Trial")) return "mu-status-trial";
    if (statusInfo.label === "Active") return "mu-status-active";
    if (statusInfo.label === "Pending") return "mu-status-pending";
    if (statusInfo.label === "Inactive") return "mu-status-neutral";
    return "mu-status-danger";
  };

  const td = {
    padding: "10px 14px",
    borderBottom: "1px solid #e3edf5",
    verticalAlign: "middle",
    fontSize: "14px",
  };

  const creditUsageTd = {
    ...td,
    color: isDarkMode ? "#dbe7f3" : "#1f2937",
    borderBottom: `1px solid ${creditUsageTheme.borderSoft}`,
  };

  // ── Modal helpers ─────────────────────────────────────────────────────────────
  const openModal = (u) => {
    setModalUserId(u.id);
    setDeleteConfirmInput("");
    setDeleteConfirmOpen(false);
    setModalActionMsg(null);
    setModalExtraTimeInput(String(u.extraTimePercentage ?? 0));
    setModalExtraTimeMsg(null);
    // Clear any stale credit input for this user
    setCreditInputs((prev) => ({ ...prev, [u.id]: "" }));
  };

  const closeModal = () => {
    setModalUserId(null);
    setDeleteConfirmInput("");
    setDeleteConfirmOpen(false);
    setModalActionMsg(null);
    setModalExtraTimeMsg(null);
  };

  const showModalMsg = (type, text) => {
    setModalActionMsg({ type, text });
    setTimeout(() => setModalActionMsg(null), 4000);
  };

  // ── Credit helpers (unchanged logic) ─────────────────────────────────────────
  const showCreditMessage = (userId, type, text) => {
    setCreditMessages((prev) => ({ ...prev, [userId]: { type, text } }));
    setTimeout(() => {
      setCreditMessages((prev) => {
        const n = { ...prev };
        delete n[userId];
        return n;
      });
    }, 3000);
  };

  const logCreditTransaction = async (
    teacherUid,
    teacherEmail,
    previousCredits,
    newCredits
  ) => {
    try {
      const auth = getAuth();
      const adminUser = auth.currentUser;
      await addDoc(collection(db, "creditTransactions"), {
        teacherUid,
        teacherEmail,
        adminUid: adminUser?.uid || "unknown",
        adjustmentAmount: newCredits - previousCredits,
        previousCredits,
        newCredits,
        type: "admin_adjustment",
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error logging credit transaction:", error);
    }
  };

  /**
   * handleAdjustCredits — only touches creditsRemaining.
   * Manual admin adjustments do NOT affect creditsSpent. (unchanged)
   */
  const handleAdjustCredits = async (
    userId,
    teacherEmail,
    adjustAmount,
    operationType = "add"
  ) => {
    const currentUser = users.find((u) => u.id === userId);
    if (!currentUser) return;

    const previousCredits = Number.isFinite(Number(currentUser.creditsRemaining))
      ? Number(currentUser.creditsRemaining)
      : 0;

    const amount = Number(adjustAmount);
    let newCredits;

    if (operationType === "add") newCredits = previousCredits + amount;
    else if (operationType === "remove")
      newCredits = Math.max(0, previousCredits - amount);
    else if (operationType === "set") newCredits = amount;

    if (!Number.isInteger(newCredits) || newCredits < 0) {
      showCreditMessage(
        userId,
        "error",
        "Invalid amount — must be a whole number ≥ 0"
      );
      return;
    }

    setCreditLoading((prev) => ({ ...prev, [userId]: true }));

    try {
      const delta = newCredits - previousCredits;
      const updateData =
        operationType === "set"
          ? { creditsRemaining: newCredits }
          : { creditsRemaining: increment(delta) };

      await updateDoc(doc(db, "users", userId), updateData);

      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, creditsRemaining: newCredits } : u
        )
      );

      setCreditInputs((prev) => {
        const n = { ...prev };
        delete n[userId];
        return n;
      });

      await logCreditTransaction(userId, teacherEmail, previousCredits, newCredits);
      showCreditMessage(userId, "success", `${previousCredits} → ${newCredits}`);
    } catch (error) {
      console.error("Error adjusting credits:", error);
      showCreditMessage(userId, "error", "Failed to update credits");
    } finally {
      setCreditLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  // ── Teacher code generator ────────────────────────────────────────────────────
  const generateTeacherCode = (email) => {
    const part = (email || "")
      .split("@")[0]
      .slice(0, 6)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "");
    const rand = Math.random().toString(36).slice(2, 5).toUpperCase();
    return `AI-${part || "USER"}-${rand}`;
  };

  // ── New teacher-status handlers ───────────────────────────────────────────────
  const handleActivateTeacher = async (uid) => {
    try {
      const cu = users.find((u) => u.id === uid);
      const teacherCode = cu?.teacherCode || generateTeacherCode(cu?.email);
      const tenant = cu?.tenant || `teacher_${uid}`;
      await updateDoc(doc(db, "users", uid), {
        role: "teacher",
        teacherStatus: "active",
        onboardingCompleted: true,
        teacherCode,
        tenant,
        updatedAt: serverTimestamp(),
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === uid
            ? {
                ...u,
                role: "teacher",
                teacherStatus: "active",
                onboardingCompleted: true,
                teacherCode,
                tenant,
              }
            : u
        )
      );
      showModalMsg("success", "✓ Teacher activated successfully.");
    } catch (e) {
      console.error(e);
      showModalMsg("error", "Failed to activate teacher.");
    }
  };

  const handleSuspendTeacher = async (uid, username) => {
    if (
      !window.confirm(
        `Suspend ${username}? They will lose access to teacher features.`
      )
    )
      return;
    try {
      await updateDoc(doc(db, "users", uid), {
        teacherStatus: "suspended",
        updatedAt: serverTimestamp(),
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === uid ? { ...u, teacherStatus: "suspended" } : u
        )
      );
      showModalMsg("success", "✓ Teacher suspended.");
    } catch (e) {
      console.error(e);
      showModalMsg("error", "Failed to suspend teacher.");
    }
  };

  const handleResetTrial = async (uid, username) => {
    if (
      !window.confirm(
        `Reset trial for ${username}? This will set 20 credits and a new 4-week trial period.`
      )
    )
      return;
    const cu = users.find((u) => u.id === uid);
    const now = new Date();
    const trialEndsAt = new Date(now.getTime() + TEACHER_TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000);
    const teacherCode = cu?.teacherCode || generateTeacherCode(cu?.email);
    const tenant = cu?.tenant || `teacher_${uid}`;
    try {
      await updateDoc(doc(db, "users", uid), {
        role: "teacher",
        onboardingCompleted: true,
        teacherStatus: "trial",
        creditsRemaining: 20,
        creditsSpent: 0,
        trialStartedAt: now,
        trialEndsAt,
        teacherCode,
        tenant,
        updatedAt: serverTimestamp(),
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === uid
            ? {
                ...u,
                role: "teacher",
                onboardingCompleted: true,
                teacherStatus: "trial",
                creditsRemaining: 20,
                creditsSpent: 0,
                trialStartedAt: now,
                trialEndsAt,
                teacherCode,
                tenant,
              }
            : u
        )
      );
      showModalMsg("success", "✓ Trial reset — 20 credits, 4 weeks from now.");
    } catch (e) {
      console.error(e);
      showModalMsg("error", "Failed to reset trial.");
    }
  };

  const handleMakeTeacherTrial = async (uid) => {
    if (
      !window.confirm(
        "Convert this user to a trial teacher? (20 credits, 4-week trial)"
      )
    )
      return;
    const cu = users.find((u) => u.id === uid);
    const now = new Date();
    const trialEndsAt = new Date(now.getTime() + TEACHER_TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000);
    const teacherCode = cu?.teacherCode || generateTeacherCode(cu?.email);
    const tenant = cu?.tenant || `teacher_${uid}`;
    try {
      await updateDoc(doc(db, "users", uid), {
        role: "teacher",
        onboardingCompleted: true,
        teacherStatus: "trial",
        creditsRemaining: 20,
        creditsSpent: 0,
        trialStartedAt: now,
        trialEndsAt,
        teacherCode,
        tenant,
        updatedAt: serverTimestamp(),
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === uid
            ? {
                ...u,
                role: "teacher",
                onboardingCompleted: true,
                teacherStatus: "trial",
                creditsRemaining: 20,
                creditsSpent: 0,
                trialStartedAt: now,
                trialEndsAt,
                teacherCode,
                tenant,
              }
            : u
        )
      );
      showModalMsg("success", "✓ Converted to trial teacher — 20 credits, 4-week trial.");
    } catch (e) {
      console.error(e);
      showModalMsg("error", "Failed to convert to teacher trial.");
    }
  };

  const handleMakeStudent = async (uid, username) => {
    if (
      !window.confirm(
        `Change ${username} to Student? Their teacher/credit history will be preserved.`
      )
    )
      return;
    try {
      await updateDoc(doc(db, "users", uid), {
        role: "student",
        onboardingCompleted: true,
        updatedAt: serverTimestamp(),
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === uid
            ? { ...u, role: "student", onboardingCompleted: true }
            : u
        )
      );
      showModalMsg("success", "✓ Role changed to Student.");
    } catch (e) {
      console.error(e);
      showModalMsg("error", "Failed to change role.");
    }
  };

  const handleMakeAdmin = async (uid, username) => {
    if (
      !window.confirm(
        `Give admin access to ${username}? This grants full admin privileges and cannot easily be undone.`
      )
    )
      return;
    try {
      await updateDoc(doc(db, "users", uid), {
        role: "admin",
        onboardingCompleted: true,
        updatedAt: serverTimestamp(),
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === uid
            ? { ...u, role: "admin", onboardingCompleted: true }
            : u
        )
      );
      showModalMsg("success", "✓ Role changed to Admin.");
    } catch (e) {
      console.error(e);
      showModalMsg("error", "Failed to change role.");
    }
  };

  const handleDeleteInModal = async () => {
    if (!modalUser || deleteConfirmInput !== modalUser.email) return;
    try {
      await deleteDoc(doc(db, "users", modalUser.id));
      setUsers((prev) => prev.filter((u) => u.id !== modalUser.id));
      closeModal();
    } catch (e) {
      console.error(e);
      showModalMsg("error", "Failed to delete user.");
    }
  };

  const handleSaveExtraTime = async () => {
    if (!modalUser) return;
    const newVal = Number(modalExtraTimeInput);
    if (isNaN(newVal) || newVal < 0 || !Number.isInteger(newVal)) {
      setModalExtraTimeMsg({ type: "error", text: "Enter a whole number ≥ 0" });
      setTimeout(() => setModalExtraTimeMsg(null), 3000);
      return;
    }
    try {
      await updateDoc(doc(db, "users", modalUser.id), {
        extraTimePercentage: newVal,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === modalUser.id ? { ...u, extraTimePercentage: newVal } : u
        )
      );
      setModalExtraTimeMsg({ type: "success", text: "✓ Saved" });
      setTimeout(() => setModalExtraTimeMsg(null), 3000);
    } catch (e) {
      console.error(e);
      setModalExtraTimeMsg({ type: "error", text: "Failed to save." });
    }
  };

  // ── Modal render ──────────────────────────────────────────────────────────────
  const renderModal = () => {
    if (!modalUser) return null;
    const mu = modalUser;
    const isTeacher = mu.role === "teacher";
    const isStudent = mu.role === "student";
    const isAdmin = mu.role === "admin";
    const statusInfo = getStatusInfo(mu);
    const safeSpent =
      typeof mu.creditsSpent === "number" &&
      isFinite(mu.creditsSpent) &&
      mu.creditsSpent >= 0
        ? mu.creditsSpent
        : 0;

    /* ── Section title ── */
    const sectionTitle = (text) => (
      <div
        className="mu-modal-section-title"
        style={{
          fontSize: "10px",
          fontWeight: "700",
          letterSpacing: "0.9px",
          textTransform: "uppercase",
          color: "#999",
          borderBottom: "1px solid #edf2f7",
          paddingBottom: "6px",
          marginBottom: "14px",
          marginTop: "4px",
        }}
      >
        {text}
      </div>
    );

    /* ── Info row ── */
    const infoRow = (label, value) => (
      <div
        className="mu-modal-info-row"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "9px",
          fontSize: "13px",
          gap: "12px",
        }}
      >
        <span style={{ color: "#888", minWidth: "120px", flexShrink: 0 }}>
          {label}
        </span>
        <span
          style={{
            color: "#1a202c",
            fontWeight: "500",
            textAlign: "right",
            wordBreak: "break-all",
          }}
        >
          {value ?? "—"}
        </span>
      </div>
    );

    /* ── Small action button ── */
    const actionBtn = (label, onClick, bg, color, title) => (
      <button
        type="button"
        className="mu-modal-action"
        onClick={onClick}
        title={title}
        style={{
          padding: "7px 13px",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: "12px",
          fontWeight: "600",
          backgroundColor: bg,
          color,
          whiteSpace: "nowrap",
          transition: "opacity 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.82")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        {label}
      </button>
    );

    return (
      <>
        {/* Backdrop */}
        <div
          className="mu-modal-backdrop"
          onClick={closeModal}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.50)",
            zIndex: 2000,
          }}
        />

        {/* Panel */}
        <div
          className="mu-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mu-modal-title"
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(580px, 95vw)",
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "#fff",
            borderRadius: "14px",
            boxShadow:
              "0 25px 60px rgba(0,0,0,0.22), 0 4px 12px rgba(0,0,0,0.10)",
            zIndex: 2001,
            fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
          }}
        >
          {/* ── Modal header ── */}
          <div
            className="mu-modal-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "18px 24px 14px",
              borderBottom: "1px solid #edf2f7",
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
              borderRadius: "14px 14px 0 0",
            }}
          >
            <div>
              <div
                id="mu-modal-title"
                className="mu-modal-title"
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: PRIMARY_DARK,
                }}
              >
                {mu.username}
              </div>
              <div
                style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}
              >
                {mu.email}
              </div>
            </div>
            <button
              type="button"
              className="mu-modal-close"
              onClick={closeModal}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "24px",
                color: "#aaa",
                lineHeight: 1,
                padding: "0 4px",
              }}
              title="Close"
              onMouseEnter={(e) => (e.currentTarget.style.color = "#555")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#aaa")}
            >
              ×
            </button>
          </div>

          {/* ── Modal body ── */}
          <div className="mu-modal-body" style={{ padding: "20px 24px 24px" }}>

            {/* Global action message */}
            {modalActionMsg && (
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: "7px",
                  marginBottom: "16px",
                  fontSize: "13px",
                  fontWeight: "500",
                  backgroundColor:
                    modalActionMsg.type === "success" ? "#d4edda" : "#f8d7da",
                  color:
                    modalActionMsg.type === "success" ? "#155724" : "#721c24",
                  border: `1px solid ${
                    modalActionMsg.type === "success" ? "#c3e6cb" : "#f5c6cb"
                  }`,
                }}
              >
                {modalActionMsg.text}
              </div>
            )}

            {/* ════════════════════════════════════════════════
                SECTION 1: Account Overview
            ═══════════════════════════════════════════════ */}
            {sectionTitle("Account Overview")}

            {infoRow("Username", mu.username)}
            {infoRow("Email", mu.email)}
            {infoRow(
              "UID",
              <span
                style={{
                  fontSize: "11px",
                  fontFamily: "monospace",
                  cursor: "pointer",
                  color: PRIMARY,
                  textDecoration: "underline",
                }}
                onClick={() => {
                  navigator.clipboard?.writeText(mu.id);
                  showModalMsg("success", "UID copied to clipboard.");
                }}
                title="Click to copy UID"
              >
                {mu.id}
              </span>
            )}
            {infoRow(
              "Role",
              <span className={roleClassFor(mu.role)} style={getRoleBadge(mu.role)}>
                {(mu.role || "N/A").charAt(0).toUpperCase() +
                  (mu.role || "N/A").slice(1)}
              </span>
            )}
            {infoRow(
              "Status",
              statusInfo ? (
                <span
                  style={{
                    display: "inline-block",
                    padding: "3px 10px",
                    borderRadius: "50px",
                    fontSize: "12px",
                    fontWeight: "600",
                    backgroundColor: statusInfo.bg,
                    color: statusInfo.color,
                  }}
                >
                  {statusInfo.label}
                </span>
              ) : (
                "—"
              )
            )}
            {infoRow("Joined", formatDate(mu.createdAt))}
            {infoRow(
              "Onboarding",
              mu.onboardingCompleted ? (
                <span style={{ color: "#155724" }}>✓ Completed</span>
              ) : (
                <span style={{ color: "#856404" }}>Not completed</span>
              )
            )}

            {/* ════════════════════════════════════════════════
                SECTION 2: Student Support (students only)
            ═══════════════════════════════════════════════ */}
            {isStudent && (
              <div style={{ marginTop: "22px" }}>
                {sectionTitle("Student Support")}
                <div
                  style={{
                    fontSize: "13px",
                    color: "#555",
                    marginBottom: "10px",
                  }}
                >
                  Extra time:{" "}
                  <strong>{mu.extraTimePercentage ?? 0}%</strong>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <input
                    type="number"
                    min="0"
                    value={modalExtraTimeInput}
                    onChange={(e) => setModalExtraTimeInput(e.target.value)}
                    style={{
                      width: "72px",
                      padding: "6px 8px",
                      border: "1px solid #c5d9e6",
                      borderRadius: "5px",
                      fontSize: "13px",
                      textAlign: "center",
                      fontFamily: "inherit",
                    }}
                  />
                  <span style={{ fontSize: "12px", color: "#666" }}>%</span>
                  {actionBtn(
                    "Save Extra Time",
                    handleSaveExtraTime,
                    "#fff4cc",
                    "#856404"
                  )}
                  {modalExtraTimeMsg && (
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color:
                          modalExtraTimeMsg.type === "success"
                            ? "#155724"
                            : "#721c24",
                      }}
                    >
                      {modalExtraTimeMsg.text}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════
                SECTION 3: Teacher Access
                (shown for teachers and students who can be promoted)
            ═══════════════════════════════════════════════ */}
            {(isTeacher || isStudent) && (
              <div style={{ marginTop: "22px" }}>
                {sectionTitle("Teacher Access")}

                {isTeacher && (
                  <>
                    {infoRow(
                      "Teacher Status",
                      statusInfo ? (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 9px",
                            borderRadius: "50px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: statusInfo.bg,
                            color: statusInfo.color,
                          }}
                        >
                          {statusInfo.label}
                        </span>
                      ) : (
                        "—"
                      )
                    )}
                    {infoRow("Trial Started", formatDate(mu.trialStartedAt))}
                    {infoRow("Trial Ends", formatDate(mu.trialEndsAt))}
                    {infoRow("Teacher Code", mu.teacherCode || "—")}
                    {infoRow("Tenant", mu.tenant || "—")}

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "8px",
                        marginTop: "12px",
                        alignItems: "center",
                      }}
                    >
                      {mu.teacherStatus === "active" ? (
                        <span
                          style={{
                            padding: "7px 13px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: "#f0f0f0",
                            color: "#999",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ✓ Already active
                        </span>
                      ) : (
                        actionBtn(
                          "✓ Activate Teacher",
                          () => handleActivateTeacher(mu.id),
                          "#d4edda",
                          "#155724",
                          "Set teacher status to Active"
                        )
                      )}
                      {mu.teacherStatus !== "suspended" &&
                        actionBtn(
                          "⊘ Suspend Teacher",
                          () => handleSuspendTeacher(mu.id, mu.username),
                          "#f8d7da",
                          "#721c24",
                          "Suspend this teacher"
                        )}
                      {actionBtn(
                        "↺ Reset Trial",
                        () => handleResetTrial(mu.id, mu.username),
                        "#fff3cd",
                        "#856404",
                        "Reset to a 4-week trial with 20 credits"
                      )}
                    </div>
                  </>
                )}

                {isStudent && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {actionBtn(
                      "🎓 Make Teacher Trial",
                      () => handleMakeTeacherTrial(mu.id),
                      "#c2dbff",
                      "#0b5cad",
                      "Convert to trial teacher (20 credits, 4 weeks)"
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ════════════════════════════════════════════════
                SECTION 4: Credits (teachers only)
            ═══════════════════════════════════════════════ */}
            {isTeacher && (
              <div style={{ marginTop: "22px" }}>
                {sectionTitle("Credits")}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    marginBottom: "12px",
                  }}
                >
                  <div>
                    <span
                      style={{
                        fontSize: "22px",
                        fontWeight: "700",
                        color: PRIMARY_DARK,
                      }}
                    >
                      {Number.isFinite(mu.creditsRemaining)
                        ? mu.creditsRemaining
                        : 0}
                    </span>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginLeft: "4px",
                      }}
                    >
                      remaining
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#aaa",
                    }}
                  >
                    {safeSpent} spent
                  </div>
                  {actionBtn(
                    "View Usage",
                    () => handleViewCreditUsageForUser(mu),
                    "#eaf3fa",
                    PRIMARY_DARK,
                    "Filter the Credit Usage table to this user"
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexWrap: "wrap",
                  }}
                >
                  <input
                    type="number"
                    min="0"
                    value={
                      creditInputs[mu.id] !== undefined
                        ? creditInputs[mu.id]
                        : ""
                    }
                    onChange={(e) =>
                      setCreditInputs({ ...creditInputs, [mu.id]: e.target.value })
                    }
                    placeholder="#"
                    disabled={creditLoading[mu.id]}
                    style={{
                      width: "68px",
                      padding: "7px 8px",
                      border: "1px solid #c5d9e6",
                      borderRadius: "5px",
                      fontSize: "13px",
                      textAlign: "center",
                      fontFamily: "inherit",
                    }}
                  />
                  {[
                    { label: "Add", op: "add", bg: "#d6eaf8", col: PRIMARY_DARK },
                    { label: "Remove", op: "remove", bg: "#ffe6e6", col: "#b52b27" },
                    { label: "Set", op: "set", bg: "#fff4e6", col: "#b26200" },
                  ].map(({ label, op, bg, col }) => (
                    <button
                      key={op}
                      disabled={creditLoading[mu.id]}
                      title={
                        op === "add"
                          ? "Add credits"
                          : op === "remove"
                          ? "Remove credits"
                          : "Set exact credits"
                      }
                      onClick={() => {
                        const amount = parseInt(creditInputs[mu.id], 10);
                        if (op !== "set" && (isNaN(amount) || amount <= 0)) {
                          showCreditMessage(mu.id, "error", "Enter a valid amount");
                          return;
                        }
                        if (op === "set" && isNaN(amount)) {
                          showCreditMessage(mu.id, "error", "Enter a valid amount");
                          return;
                        }
                        handleAdjustCredits(mu.id, mu.email, amount, op);
                      }}
                      style={{
                        padding: "7px 13px",
                        border: "none",
                        borderRadius: "5px",
                        cursor: creditLoading[mu.id] ? "not-allowed" : "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                        backgroundColor: creditLoading[mu.id] ? "#e0e0e0" : bg,
                        color: creditLoading[mu.id] ? "#999" : col,
                        whiteSpace: "nowrap",
                        transition: "opacity 0.15s",
                      }}
                    >
                      {creditLoading[mu.id] ? "…" : label}
                    </button>
                  ))}
                </div>

                {creditMessages[mu.id] && (
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "5px 10px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "500",
                      backgroundColor:
                        creditMessages[mu.id].type === "success"
                          ? "#d6eaf8"
                          : "#ffe6e6",
                      color:
                        creditMessages[mu.id].type === "success"
                          ? PRIMARY_DARK
                          : "#b52b27",
                      display: "inline-block",
                    }}
                  >
                    {creditMessages[mu.id].text}
                  </div>
                )}
              </div>
            )}

            {/* ════════════════════════════════════════════════
                SECTION 5: Role Management (admin only)
            ═══════════════════════════════════════════════ */}
            {user?.role === "admin" && (
              <div style={{ marginTop: "22px" }}>
                {sectionTitle("Role Management")}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {!isStudent &&
                    actionBtn(
                      "📚 Make Student",
                      () => handleMakeStudent(mu.id, mu.username),
                      "#d6eaf8",
                      PRIMARY_DARK,
                      "Change role to Student"
                    )}
                  {!isTeacher &&
                    actionBtn(
                      "🎓 Make Teacher Trial",
                      () => handleMakeTeacherTrial(mu.id),
                      "#c2dbff",
                      "#0b5cad",
                      "Convert to trial teacher"
                    )}
                  {!isAdmin &&
                    actionBtn(
                      "⭐ Make Admin",
                      () => handleMakeAdmin(mu.id, mu.username),
                      "#f7dd8f",
                      "#876600",
                      "Grant admin privileges"
                    )}
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════
                SECTION 6: Danger Zone — Delete only (admin only)
            ═══════════════════════════════════════════════ */}
            {user?.role === "admin" && (
              <div
                className="mu-danger-zone"
                style={{
                  marginTop: "20px",
                  border: "1px solid #f5c6cb",
                  borderRadius: "8px",
                  padding: "16px",
                  backgroundColor: "#fffbfb",
                }}
              >
                {sectionTitle("⚠ Danger Zone")}

                {/* Delete with email confirmation */}
                {!deleteConfirmOpen ? (
                  <button
                    onClick={() => setDeleteConfirmOpen(true)}
                    style={{
                      padding: "7px 13px",
                      border: "1.5px solid #b52b27",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      backgroundColor: "transparent",
                      color: "#b52b27",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#b52b27";
                      e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#b52b27";
                    }}
                  >
                    🗑 Delete User
                  </button>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#fff5f5",
                      border: "1px solid #f5c6cb",
                      borderRadius: "7px",
                      padding: "14px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#721c24",
                        marginBottom: "10px",
                        fontWeight: "500",
                        lineHeight: "1.5",
                      }}
                    >
                      ⚠ This action is <strong>permanent</strong> and cannot be
                      undone. Type{" "}
                      <code
                        style={{
                          backgroundColor: "#fde8e8",
                          padding: "1px 5px",
                          borderRadius: "3px",
                          fontSize: "12px",
                        }}
                      >
                        {mu.email}
                      </code>{" "}
                      to confirm deletion.
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <input
                        type="email"
                        placeholder={mu.email}
                        value={deleteConfirmInput}
                        onChange={(e) => setDeleteConfirmInput(e.target.value)}
                        autoFocus
                        style={{
                          flex: 1,
                          minWidth: "180px",
                          padding: "7px 10px",
                          border: "1px solid #f5c6cb",
                          borderRadius: "5px",
                          fontSize: "13px",
                          fontFamily: "inherit",
                          outline: "none",
                        }}
                      />
                      <button
                        disabled={deleteConfirmInput !== mu.email}
                        onClick={handleDeleteInModal}
                        style={{
                          padding: "7px 14px",
                          border: "none",
                          borderRadius: "6px",
                          cursor:
                            deleteConfirmInput === mu.email
                              ? "pointer"
                              : "not-allowed",
                          fontSize: "12px",
                          fontWeight: "700",
                          backgroundColor:
                            deleteConfirmInput === mu.email
                              ? "#b52b27"
                              : "#e0e0e0",
                          color:
                            deleteConfirmInput === mu.email ? "#fff" : "#999",
                          whiteSpace: "nowrap",
                          transition: "background 0.15s",
                        }}
                      >
                        Confirm Delete
                      </button>
                      <button
                        onClick={() => {
                          setDeleteConfirmOpen(false);
                          setDeleteConfirmInput("");
                        }}
                        style={{
                          padding: "7px 11px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          backgroundColor: "#fff",
                          color: "#555",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const handleLogout = async () => {
    try {
      await getAuth().signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out", error);
    }
  };

  const adminName = accountDisplayName(user, "StudyDen administrator");

  // ── Main render ───────────────────────────────────────────────────────────────
  return (
    <div className={`sd-dashboard sd-role-admin mu-page${darkMode ? " sd-dark" : ""}`}>
      <DashboardSidebar
        role="admin"
        username={adminName}
        creditsRemaining={null}
        teacherCode=""
        onCopyCode={() => {}}
        copied={false}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/manage-users"
      />
      <div className="sd-workspace mu-workspace">
        <DashboardHeader
          username={adminName}
          role="admin"
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)}
          search={searchTerm}
          onSearch={setSearchTerm}
        />
        <main className="mu-main">
          <section className="mu-hero" aria-labelledby="mu-page-title">
            <span className="mu-hero-arc mu-hero-arc-top" aria-hidden="true" />
            <span className="mu-hero-arc mu-hero-arc-bottom" aria-hidden="true" />
            <div className="mu-hero-copy">
              <button className="mu-hero-back" type="button" onClick={() => navigate("/dashboard")}>
                <span aria-hidden="true">←</span> Dashboard
              </button>
              <p className="mu-eyebrow">User management</p>
              <h1 id="mu-page-title">Manage Users</h1>
              <p>Search, review and manage StudyDen accounts, roles, trials and credits.</p>
            </div>
            <div className="mu-hero-art" aria-hidden="true">
              <span className="mu-hero-icon"><Users size={46} /></span>
              <i className="mu-orbit mu-orbit-one"><ShieldCheck size={19} /></i>
              <i className="mu-orbit mu-orbit-two"><UserCog size={19} /></i>
              <i className="mu-orbit mu-orbit-three"><Sparkles size={18} /></i>
            </div>
          </section>
          <div className="mu-content">
        {/* ── Header ── */}
        <div
          className="mu-legacy-heading"
          style={{
            textAlign: "center",
            marginBottom: "24px",
            paddingBottom: "16px",
            borderBottom: `2px solid ${PRIMARY_LIGHT_BG}`,
          }}
        >
          <h1
            style={{
              margin: 0,
              color: PRIMARY_DARK,
              fontSize: "28px",
              fontWeight: "700",
            }}
          >
            👥 Manage Users
          </h1>
        </div>

        {/* ── Stat cards ── */}
        <div
          className="mu-metrics"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          {[
            { label: "Total Users", value: userStats.total, tone: "blue", icon: Users },
            { label: "Teachers", value: userStats.teachers, tone: "teal", icon: GraduationCap },
            { label: "Students", value: userStats.students, tone: "purple", icon: UserRound },
            { label: "Admins", value: userStats.admins, tone: "amber", icon: ShieldCheck },
            { label: "Active Trials", value: userStats.activeTrials, tone: "green", icon: Activity },
            { label: "New This Week", value: userStats.newThisWeek, tone: "cyan", icon: CalendarDays },
          ].map(({ label, value, tone, icon: Icon }) => (
            <div
              key={label}
              className={`mu-metric mu-tone-${tone}`}
              style={{
                backgroundColor: PRIMARY_LIGHT_BG,
                padding: "14px",
                borderRadius: "8px",
                textAlign: "center",
                border: "1px solid #d1e5f0",
              }}
            >
              <span className="mu-metric-icon"><Icon size={19} /></span>
              <p
                className="mu-metric-value"
                style={{
                  fontSize: "26px",
                  fontWeight: "700",
                  color: PRIMARY_DARK,
                  margin: "0 0 4px 0",
                }}
              >
                {value}
              </p>
              <p
                className="mu-metric-label"
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "#666",
                  margin: 0,
                }}
              >
                {label}
              </p>
            </div>
          ))}
        </div>

        {/* ── Filter tabs ── */}
        <div
          className="mu-filter-tabs"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          {[
            { value: "all", label: "All" },
            { value: "students", label: "Students" },
            { value: "teachers", label: "Teachers" },
            { value: "admins", label: "Admins" },
            { value: "joined-this-week", label: "Joined This Week" },
          ].map(({ value, label }) => {
            const active = selectedFilter === value;
            return (
              <button
                key={value}
                type="button"
                className={`mu-filter-button${active ? " active" : ""}`}
                aria-pressed={active}
                onClick={() => setSelectedFilter(value)}
                style={{
                  padding: "7px 14px",
                  background: active
                    ? `linear-gradient(135deg, ${PRIMARY} 0%, #3498db 100%)`
                    : "#fff",
                  color: active ? "#fff" : PRIMARY,
                  border: active ? "none" : `1.5px solid ${PRIMARY}`,
                  borderRadius: "6px",
                  fontWeight: active ? "600" : "500",
                  fontSize: "13px",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: active ? "0 2px 6px rgba(41,128,185,0.2)" : "none",
                  transition: "all 0.2s",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* ── Search + Sort ── */}
        <div
          className="mu-toolbar"
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "18px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <label className="mu-search-wrap">
            <Search size={17} aria-hidden="true" />
            <input
              className="mu-search-input"
              type="text"
              aria-label="Search users by name, email, or role"
              placeholder="Search users by name, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: "200px",
                padding: "9px 13px",
                border: "1px solid #c5d9e6",
                borderRadius: "6px",
                fontSize: "14px",
                fontFamily: "inherit",
              }}
            />
          </label>
          <select
            className="mu-sort-select"
            aria-label="Sort users"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            style={{
              padding: "9px 13px",
              border: "1px solid #c5d9e6",
              borderRadius: "6px",
              fontSize: "14px",
              backgroundColor: "#fff",
              cursor: "pointer",
              minWidth: "140px",
            }}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name-az">Name A–Z</option>
            <option value="name-za">Name Z–A</option>
            <option value="role">Role</option>
          </select>
        </div>

        {user?.role === "admin" && (
          <section
            ref={creditUsageSectionRef}
            className={`mu-credit-usage${creditUsageHighlighted ? " is-highlighted" : ""}`}
            style={{
              marginBottom: "26px",
              padding: 0,
              background: creditUsageTheme.surface,
              border: `1px solid ${creditUsageTheme.border}`,
              borderRadius: "16px",
              boxShadow: creditUsageHighlighted
                ? `0 0 0 3px ${isDarkMode ? "rgba(56,189,248,0.22)" : "rgba(41,128,185,0.22)"}, ${creditUsageTheme.shadow}`
                : creditUsageTheme.shadow,
              transition: "box-shadow 0.3s ease",
              scrollMarginTop: "20px",
              overflow: "hidden",
            }}
          >
            <button
              className="mu-credit-toggle"
              type="button"
              aria-expanded={showCreditUsage}
              aria-controls="credit-usage-panel"
              onClick={() => setShowCreditUsage((prev) => !prev)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
                alignItems: "center",
                width: "100%",
                padding: "18px",
                border: "none",
                borderBottom: showCreditUsage ? `1px solid ${creditUsageTheme.borderSoft}` : "none",
                background: isDarkMode
                  ? "linear-gradient(135deg, rgba(15,27,45,0.98), rgba(19,35,58,0.96))"
                  : "linear-gradient(135deg, #ffffff, #f8fbff)",
                cursor: "pointer",
                textAlign: "left",
                fontFamily: "inherit",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <h2 className="mu-section-title"
                  style={{
                    margin: 0,
                    color: creditUsageTheme.title,
                    fontSize: "20px",
                    fontWeight: "700",
                  }}
                >
                  Credit Usage
                </h2>
                <p
                  style={{
                    margin: 0,
                    color: creditUsageTheme.text,
                    fontSize: "13px",
                  }}
                >
                  Latest 100 usage logs. Click to view recent credit activity.
                </p>
                <span
                  style={{
                    color: creditUsageTheme.text,
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  {creditUsageLoading
                    ? "Loading logs..."
                    : `${creditUsageLogs.length} log${creditUsageLogs.length === 1 ? "" : "s"} loaded`}
                </span>
              </div>

              <div
                className="mu-credit-toggle-action"
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "9px 13px",
                    border: `1px solid ${creditUsageTheme.border}`,
                    borderRadius: "999px",
                    backgroundColor: creditUsageTheme.accentSoft,
                    color: creditUsageTheme.accent,
                    fontSize: "14px",
                    fontWeight: "700",
                  }}
                >
                  {showCreditUsage ? "Hide Credit Usage" : "Show Credit Usage"}
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-block",
                      transform: showCreditUsage ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    ▼
                  </span>
                </span>
              </div>
            </button>

            {showCreditUsage && (
              <div
                id="credit-usage-panel"
                className="mu-credit-panel"
                style={{
                  padding: "18px",
                  transition: "opacity 0.2s ease",
                }}
              >
                <div
                  className="mu-credit-filters"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: "14px",
                  }}
                >
                  <input
                    className="mu-control"
                    type="text"
                    placeholder="Search teacher or email..."
                    value={creditUsageSearch}
                    onChange={(e) => setCreditUsageSearch(e.target.value)}
                    style={{
                      minWidth: "220px",
                      padding: "9px 13px",
                      border: `1px solid ${isDarkMode ? "rgba(148,163,184,0.26)" : "#c5d9e6"}`,
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      backgroundColor: isDarkMode ? "#0b1626" : "#fff",
                      color: isDarkMode ? "#e5eef8" : "#1f2937",
                    }}
                  />
                  <select
                    className="mu-control"
                    value={creditUsageActionFilter}
                    onChange={(e) => setCreditUsageActionFilter(e.target.value)}
                    style={{
                      padding: "9px 13px",
                      border: `1px solid ${isDarkMode ? "rgba(148,163,184,0.26)" : "#c5d9e6"}`,
                      borderRadius: "8px",
                      fontSize: "14px",
                      backgroundColor: isDarkMode ? "#0b1626" : "#fff",
                      color: isDarkMode ? "#e5eef8" : "#1f2937",
                      cursor: "pointer",
                      minWidth: "210px",
                    }}
                  >
                    {creditActionOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

            {creditUsageError ? (
              <div
                className="mu-table-scroll mu-credit-table-scroll"
                style={{
                  padding: "14px",
                  borderRadius: "8px",
                  backgroundColor: "#f8d7da",
                  color: "#721c24",
                  fontSize: "14px",
                }}
              >
                {creditUsageError}
              </div>
            ) : creditUsageLoading ? (
              <div
                style={{
                  padding: "24px 0",
                  color: PRIMARY,
                  textAlign: "center",
                  fontSize: "15px",
                }}
              >
                Loading credit usage...
              </div>
            ) : displayedCreditUsageLogs.length > 0 ? (
              <div
                style={{
                  overflowX: "auto",
                  borderRadius: "8px",
                  border: `1px solid ${creditUsageTheme.borderSoft}`,
                }}
              >
                <table
                  className="mu-data-table mu-credit-table"
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    minWidth: "1050px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                        color: "#fff",
                      }}
                    >
                      {[
                        "Date/time",
                        "Teacher/User",
                        "Email",
                        "Action",
                        "Credits used",
                        "Balance before",
                        "Balance after",
                        "Details",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "11px 12px",
                            textAlign: "left",
                            fontWeight: "600",
                            fontSize: "12px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedCreditUsageLogs.map((log, idx) => (
                      <tr
                        key={log.id}
                        style={{
                          backgroundColor:
                            idx % 2 === 0
                              ? isDarkMode ? "#0b1626" : "#ffffff"
                              : isDarkMode ? "#101c2f" : PRIMARY_LIGHTER_BG,
                        }}
                      >
                        <td style={{ ...creditUsageTd, whiteSpace: "nowrap" }}>
                          {formatDateTime(log.createdAt)}
                        </td>
                        <td style={{ ...creditUsageTd, minWidth: "150px" }}>
                          <span
                            title={log.userId || ""}
                            style={{ fontWeight: "600", color: creditUsageTheme.accent }}
                          >
                            {log.userName || log.userEmail || log.userId || "Unknown user"}
                          </span>
                        </td>
                        <td style={{ ...creditUsageTd, minWidth: "190px", wordBreak: "break-all" }}>
                          {log.userEmail || "--"}
                        </td>
                        <td style={{ ...creditUsageTd, minWidth: "160px" }}>
                          {formatActionType(log.actionType)}
                        </td>
                        <td style={{ ...creditUsageTd, fontWeight: "700", color: creditUsageTheme.accent }}>
                          {formatCreditValue(log.creditsUsed)}
                        </td>
                        <td style={creditUsageTd}>{formatCreditValue(log.creditsBefore)}</td>
                        <td style={creditUsageTd}>{formatCreditValue(log.creditsAfter)}</td>
                        <td style={{ ...creditUsageTd, minWidth: "240px", color: isDarkMode ? "#cbd5e1" : "#4a6478" }}>
                          {formatCreditUsageDetails(log)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  padding: "22px 0",
                  fontSize: "14px",
                  color: creditUsageTheme.text,
                  textAlign: "center",
                  backgroundColor: creditUsageTheme.surfaceSoft,
                  borderRadius: "8px",
                  border: `1px dashed ${creditUsageTheme.border}`,
                }}
              >
                No credit usage logs found.
              </div>
            )}
              </div>
            )}
          </section>
        )}

        {/* ── Table ── */}
        {isLoading ? (
          <div
            className="mu-state mu-loading-state"
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: PRIMARY,
              fontSize: "18px",
            }}
          >
            Loading users…
          </div>
        ) : displayedUsers.length > 0 ? (
          <div
            className="mu-table-card"
            style={{
              overflowX: "auto",
              borderRadius: "10px",
              boxShadow: "0 4px 10px rgba(41,128,185,0.08)",
            }}
          >
            <table
              className="mu-data-table mu-users-table"
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                minWidth: "700px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                    color: "#fff",
                  }}
                >
                  {[
                    "Name / Email",
                    "Role",
                    "Status",
                    "Joined",
                    "Credits",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "12px 14px",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "13px",
                        whiteSpace: "nowrap",
                        letterSpacing: "0.3px",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {displayedUsers.map((u, idx) => {
                  const isTeacher = u.role === "teacher";
                  const rowBg = idx % 2 === 0 ? "#ffffff" : PRIMARY_LIGHTER_BG;
                  const statusInfo = getStatusInfo(u);
                  const safeSpent =
                    typeof u.creditsSpent === "number" &&
                    isFinite(u.creditsSpent) &&
                    u.creditsSpent >= 0
                      ? u.creditsSpent
                      : 0;

                  return (
                    <tr key={u.id} className="mu-user-row" style={{ backgroundColor: rowBg }}>

                      {/* Name / Email */}
                      <td className="mu-identity-cell" data-label="User" style={{ ...td, minWidth: "180px" }}>
                        <span className={`mu-avatar ${avatarToneFor(u.email)}`} aria-hidden="true">
                          {initialsFor(u.username)}
                        </span>
                        <div className="mu-identity-copy">
                        <button
                          type="button"
                          className="mu-user-link"
                          onClick={() => navigate(`/student-profile/${u.id}`)}
                          style={{
                            cursor: "pointer",
                            color: PRIMARY,
                            fontWeight: "600",
                            fontSize: "14px",
                            marginBottom: "2px",
                            textDecoration: "underline",
                          }}
                          title="View profile"
                        >
                          {u.username}
                        </button>
                        <div
                          className="mu-user-email"
                          style={{
                            color: "#888",
                            fontSize: "12px",
                            wordBreak: "break-all",
                          }}
                        >
                          {u.email}
                        </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td data-label="Role" style={{ ...td, minWidth: "90px" }}>
                        <span className={roleClassFor(u.role)} style={getRoleBadge(u.role)}>
                          {(u.role || "N/A").charAt(0).toUpperCase() +
                            (u.role || "N/A").slice(1)}
                        </span>
                      </td>

                      {/* Status */}
                      <td data-label="Status" style={{ ...td, minWidth: "120px" }}>
                        {statusInfo ? (
                          <span
                            className={`mu-status-badge ${statusClassFor(u, statusInfo)}`}
                            style={{
                              display: "inline-block",
                              padding: "3px 10px",
                              borderRadius: "50px",
                              fontSize: "12px",
                              fontWeight: "600",
                              backgroundColor: statusInfo.bg,
                              color: statusInfo.color,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {statusInfo.label}
                          </span>
                        ) : (
                          <span style={{ color: "#ccc", fontSize: "13px" }}>—</span>
                        )}
                      </td>

                      {/* Joined */}
                      <td
                        data-label="Joined"
                        style={{
                          ...td,
                          whiteSpace: "nowrap",
                          color: "#4a6478",
                          minWidth: "100px",
                        }}
                      >
                        {formatDate(u.createdAt)}
                      </td>

                      {/* Credits */}
                      <td data-label="Credits" className="mu-credit-cell" style={{ ...td, minWidth: "120px" }}>
                        {isTeacher ? (
                          <div>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: PRIMARY_DARK,
                              }}
                            >
                              {Number.isFinite(u.creditsRemaining)
                                ? u.creditsRemaining
                                : 0}{" "}
                              remaining
                            </div>
                            <div
                              style={{
                                fontSize: "12px",
                                color: safeSpent > 0 ? "#666" : "#bbb",
                                marginTop: "2px",
                              }}
                            >
                              {safeSpent} spent
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "#ccc", fontSize: "13px" }}>—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td data-label="Actions" className="mu-actions-cell" style={{ ...td, minWidth: "90px" }}>
                        <button
                          type="button"
                          className="mu-manage-button"
                          onClick={() => openModal(u)}
                          style={{
                            padding: "6px 16px",
                            background: `linear-gradient(135deg, ${PRIMARY} 0%, #3498db 100%)`,
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 5px rgba(41,128,185,0.25)",
                            transition: "opacity 0.15s",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.opacity = "0.85")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.opacity = "1")
                          }
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            className="mu-state mu-empty-state"
            style={{
              padding: "30px 0",
              fontSize: "15px",
              color: "#666",
              textAlign: "center",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
              border: "1px dashed #ddd",
            }}
          >
            No users found for the selected filter.
          </div>
        )}

        {/* ── Back to Dashboard ── */}
        <div style={{ marginTop: "28px", textAlign: "center" }}>
          <button
            className="mu-dashboard-button"
            type="button"
            onClick={() => navigate("/dashboard")}
            style={{
              padding: "11px 24px",
              background: `linear-gradient(135deg, ${PRIMARY} 0%, #3498db 100%)`,
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "15px",
              fontWeight: "500",
              boxShadow: "0 4px 10px rgba(41,128,185,0.25)",
              transition: "all 0.2s",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* ── Floating back-to-top ── */}
      <button
        className="mu-back-to-top"
        type="button"
        onClick={scrollToTop}
        aria-label="Back to top"
        title="Back to top"
        style={{
          position: "fixed",
          bottom: "30px",
          right: "30px",
          width: "48px",
          height: "48px",
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #3498db 100%)`,
          color: "#fff",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 16px rgba(41,128,185,0.35)",
          opacity: showBackToTop ? 1 : 0,
          transform: showBackToTop ? "translateY(0)" : "translateY(20px)",
          pointerEvents: showBackToTop ? "auto" : "none",
          transition: "transform 0.2s ease, opacity 0.3s ease",
          zIndex: 1000,
        }}
      >
        <ArrowUp size={20} strokeWidth={2.5} />
      </button>

      {/* ── Manage User Modal ── */}
      {renderModal()}
        </main>
      </div>
    </div>
  );
};

export default ManageUsers;
